import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  CreditCard,
  Security,
  Star,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import LandingLayout from './LandingLayout';
import ProgressBar from './ProgressBar';

interface PlanDetails {
  id: string;
  name: string;
  price: string;
  originalPrice?: string;
  savings?: string;
  features: string[];
  popular?: boolean;
}

const PaymentSummary: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [billingZip, setBillingZip] = useState('');

  // Mock plan data - in real app, this would come from previous step
  const selectedPlan: PlanDetails = {
    id: 'annual',
    name: 'Annual Plan',
    price: '$130',
    originalPrice: '$179.88',
    savings: 'Save $49.88',
    features: [
      'Daily wellness check SMS',
      'Up to 4 caregivers in care circle',
      'Two-way SMS communication',
      'Escalation alerts',
      'Vacation mode',
      'Comprehensive reporting',
      '24/7 system monitoring',
      'Priority support'
    ],
    popular: true
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validate form
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !billingZip) {
      setError('Please fill in all payment fields');
      setLoading(false);
      return;
    }

    try {
      // TODO: Integrate with Stripe payment processing
      // For now, simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      setSuccess('Payment processed successfully! Your account is now active.');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err) {
      setError('Payment processing failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCardNumber = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = matches && matches[0] || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return v;
    }
  };

  const formatExpiryDate = (value: string) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  return (
    <LandingLayout>
      <Container component="main" maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: '100%', maxWidth: 800, mb: 4 }}>
            <ProgressBar
              currentStep={5}
              totalSteps={5}
              stepLabels={['Plan', 'Account', 'Loved One', 'Care Circle', 'Payment']}
            />
          </Box>
          
          <Paper elevation={3} sx={{ p: 6, width: '100%', maxWidth: 1000 }}>
            <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
              Complete Your Setup
            </Typography>
            
            <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
              Review your plan and enter payment information to start your wellness check service
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3 }}>
                {success}
              </Alert>
            )}

            <Grid container spacing={4}>
              {/* Plan Summary */}
              <Grid item xs={12} md={5}>
                <Card sx={{ height: 'fit-content', border: '2px solid #2E7D32' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                        {selectedPlan.name}
                      </Typography>
                      {selectedPlan.popular && (
                        <Chip
                          icon={<Star />}
                          label="Most Popular"
                          color="primary"
                          size="small"
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 2 }}>
                      <Typography variant="h3" component="span" sx={{ fontWeight: 'bold', color: '#2E7D32' }}>
                        {selectedPlan.price}
                      </Typography>
                      {selectedPlan.originalPrice && (
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            textDecoration: 'line-through', 
                            color: 'text.secondary',
                            ml: 1 
                          }}
                        >
                          {selectedPlan.originalPrice}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ ml: 1 }}>
                        /year
                      </Typography>
                    </Box>

                    {selectedPlan.savings && (
                      <Chip 
                        label={selectedPlan.savings} 
                        color="success" 
                        size="small" 
                        sx={{ mb: 2 }}
                      />
                    )}

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                      What's included:
                    </Typography>

                    <Box component="ul" sx={{ pl: 0, m: 0 }}>
                      {selectedPlan.features.map((feature, index) => (
                        <Box 
                          component="li" 
                          key={index}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            mb: 1,
                            listStyle: 'none',
                          }}
                        >
                          <CheckCircle sx={{ color: '#2E7D32', mr: 1, fontSize: '1.2rem' }} />
                          <Typography variant="body2">{feature}</Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>No commitment</strong> • Cancel anytime • Secure payment processing
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* Payment Form */}
              <Grid item xs={12} md={7}>
                <Card>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      <CreditCard sx={{ color: '#2E7D32', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Payment Information
                      </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <TextField
                            required
                            fullWidth
                            label="Card Number"
                            value={cardNumber}
                            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                            placeholder="1234 5678 9012 3456"
                            disabled={loading}
                            inputProps={{ maxLength: 19 }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            fullWidth
                            label="Expiry Date"
                            value={expiryDate}
                            onChange={(e) => setExpiryDate(formatExpiryDate(e.target.value))}
                            placeholder="MM/YY"
                            disabled={loading}
                            inputProps={{ maxLength: 5 }}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            required
                            fullWidth
                            label="CVV"
                            value={cvv}
                            onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
                            placeholder="123"
                            disabled={loading}
                            inputProps={{ maxLength: 4 }}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            required
                            fullWidth
                            label="Cardholder Name"
                            value={cardholderName}
                            onChange={(e) => setCardholderName(e.target.value)}
                            placeholder="John Doe"
                            disabled={loading}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            required
                            fullWidth
                            label="Billing ZIP Code"
                            value={billingZip}
                            onChange={(e) => setBillingZip(e.target.value.replace(/\D/g, ''))}
                            placeholder="12345"
                            disabled={loading}
                            inputProps={{ maxLength: 5 }}
                          />
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 4, p: 2, bgcolor: '#f8f9fa', borderRadius: 1, border: '1px solid #e9ecef' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Security sx={{ color: '#2E7D32', mr: 1, fontSize: '1.2rem' }} />
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            Secure Payment
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Your payment information is encrypted and secure. We use Stripe for payment processing.
                        </Typography>
                      </Box>

                      <Box sx={{ textAlign: 'center', mt: 4 }}>
                        <Button
                          type="submit"
                          variant="contained"
                          size="large"
                          disabled={loading}
                          sx={{
                            py: 1.5,
                            px: 4,
                            backgroundColor: '#2E7D32',
                            '&:hover': {
                              backgroundColor: '#1B5E20'
                            },
                            fontSize: '1.1rem',
                            minWidth: 250,
                          }}
                        >
                          {loading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                              Processing Payment...
                            </Box>
                          ) : (
                            `Complete Setup - ${selectedPlan.price}/year`
                          )}
                        </Button>

                        <Box sx={{ mt: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            By completing your purchase, you agree to our Terms of Service and Privacy Policy
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Container>
    </LandingLayout>
  );
};

export default PaymentSummary;
